import { useState, useEffect } from "react";
import { Capacitor } from "@capacitor/core";
import { App } from "@capacitor/app";
import { updateAccount } from "../lib/firebase";
import { useUsersStore } from "../stores/users.store";

function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(true);
  const uid = useUsersStore(state => state.user?.userId);

  useEffect(() => {
    const handleVisibilityChange = () => {
      try {
        setIsOnline(!document.hidden);
      } catch (error) {
        console.error("Error handling visibility change:", error);
      }
    };

    const handleAppStateChanged = (state: any) => {
      try {
        setIsOnline(state.isActive);
      } catch (error) {
        console.error("Error handling app state change:", error);
      }
    };

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      try {
        setIsOnline(false);
        event.preventDefault();
        event.returnValue = "";
      } catch (error) {
        console.error("Error handling before unload:", error);
      }
    };

    let appStateListenerReference: any;

    try {
      if (Capacitor.isPluginAvailable("App")) {
        appStateListenerReference = App.addListener(
          "appStateChange",
          handleAppStateChanged
        );
      } else {
        document.addEventListener("visibilitychange", handleVisibilityChange);
        window.addEventListener("beforeunload", handleBeforeUnload);
      }
    } catch (error) {
      console.error("Error setting up event listeners:", error);
    }

    return () => {
      try {
        if (Capacitor.isPluginAvailable("App") && appStateListenerReference) {
          appStateListenerReference.remove();
        } else {
          document.removeEventListener(
            "visibilitychange",
            handleVisibilityChange
          );
          window.removeEventListener("beforeunload", handleBeforeUnload);
        }
      } catch (error) {
        console.error("Error cleaning up event listeners:", error);
      }
    };
  }, []);

  useEffect(() => {
    const updateOnlineStatus = async () => {
      try {
        if (uid) {
          await updateAccount(uid, { isOnline });
        }
      } catch (error) {
        console.error("Error updating online status:", error);
      }
    };

    if (uid) {
      updateOnlineStatus();
    }
  }, [isOnline, uid]);

  return isOnline;
}

export default useOnlineStatus;
